import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {QRCode} from "antd";
import dayjs from "dayjs";

import SignInForm from "../../forms/AuthForm/SignInForm"
import {useNavigate} from "react-router-dom";
import getUser from "../../store/utilits";
import {images} from "../../constants";
import Localization from "../Localizations/Localization";
import {ReceiptLine, RECEIPT_TYPE_TEXT} from "../Orders/ReceiptChecks/helper.receiptCheck";
import "../Orders/ReceiptChecks/ReceiptCheck.scss";
import './SignIn.scss'

const HELP_URL = 'https://t.me/bct_support';
const COMPANY_NAME = 'BCT';
const COMPANY_ADDRESS = "Toshkent, Afrosiyob ko'chasi 16";
// Receipts are printed in Uzbek whatever the UI language is, same as in ReceiptCheck
const RECEIPT_TYPES = Object.values(RECEIPT_TYPE_TEXT);
const RECEIPT_TYPE_INTERVAL_MS = 2600;
const CHEK_INTERVAL_MS = 1000;
const CHEK_DIGITS = 6;
const STIR_DIGITS = 9;
const NKM_DIGITS = 8;
const NKM_PREFIX = '100';
// The header rows only come out at the end of the receipt's print animation
const SCRAMBLE_DELAY_MS = 1500;
const SCRAMBLE_DURATION_MS = 1600;
const SCRAMBLE_HOLD_MS = 2000;
const SCRAMBLE_TICK_MS = 60;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const randomDigits = (length) => Array.from({length}, () => Math.floor(Math.random() * 10)).join('');

// Runs through random digits and stops on a random number, settling one digit at a time from the left,
// holds it for a moment and starts over. The prefix stays fixed and only the digits after it run.
const useScrambledNumber = (length, prefix = '') => {
    const randomLength = length - prefix.length;
    const [digits, setDigits] = useState(() => '0'.repeat(randomLength));

    useEffect(() => {
        if (prefersReducedMotion()) {
            setDigits(randomDigits(randomLength));
            return undefined;
        }

        let waitTimer = null;
        let scrambleTimer = null;

        const runScramble = () => {
            const finalValue = randomDigits(randomLength);
            const startedAt = Date.now();

            scrambleTimer = setInterval(() => {
                const settled = Math.floor(((Date.now() - startedAt) / SCRAMBLE_DURATION_MS) * randomLength);

                if (settled >= randomLength) {
                    clearInterval(scrambleTimer);
                    setDigits(finalValue);
                    waitTimer = setTimeout(runScramble, SCRAMBLE_HOLD_MS);
                    return;
                }

                setDigits(finalValue.slice(0, settled) + randomDigits(randomLength - settled));
            }, SCRAMBLE_TICK_MS);
        };

        waitTimer = setTimeout(runScramble, SCRAMBLE_DELAY_MS);

        return () => {
            clearTimeout(waitTimer);
            clearInterval(scrambleTimer);
        };
    }, [randomLength]);

    return prefix + digits;
};

const useChekCounter = () => {
    const [chek, setChek] = useState(1);

    useEffect(() => {
        if (prefersReducedMotion()) {
            return undefined;
        }

        const timer = setInterval(() => setChek((current) => current + 1), CHEK_INTERVAL_MS);

        return () => clearInterval(timer);
    }, []);

    return String(chek).padStart(CHEK_DIGITS, '0');
};

// Owns the interval, so changing the title never re-renders the sign-in form
const ReceiptTypeBadge = () => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((current) => (current + 1) % RECEIPT_TYPES.length);
        }, RECEIPT_TYPE_INTERVAL_MS);

        return () => clearInterval(timer);
    }, []);

    return (
        <div className='receipt__type'>
            <span key={index} className='sign-in__receipt-type'>{RECEIPT_TYPES[index]}</span>
        </div>
    );
};

// Owns the running numbers, so their updates never re-render the sign-in form
const ReceiptDetails = ({printedAt}) => {
    const chek = useChekCounter();
    const stir = useScrambledNumber(STIR_DIGITS);
    const nkm = useScrambledNumber(NKM_DIGITS, NKM_PREFIX);

    return (
        <>
            <ReceiptLine left={`Chek: ${chek}`} right={printedAt.format('DD/MM/YYYY HH:mm:ss')}/>
            <ReceiptLine left='POS: FISCALBOX-1' right='Kassir: Admin'/>
            <ReceiptLine left={`STIR: ${stir}`} right={`NKM S/R: ${nkm}`}/>
        </>
    );
};

// Same markup and classes as the Orders receipt, so these parts look exactly like it
const ReceiptPart = ({children}) => (
    <div className='detail-view__receipt sign-in__receipt-part' aria-hidden='true'>
        <div className='receipt'>
            <div className='receipt__content'>{children}</div>
        </div>
    </div>
);

const SignIn = () => {
    const signInError = useSelector(state => state.user.signInError);
    const [user, setUser] = useState(() => getUser() || null);
    const [printedAt] = useState(() => dayjs());
    const navigate = useNavigate()
    const {t} = useTranslation();

    useEffect(() => {
        if (user) {
            navigate("/analysis")
        }
    }, [user, navigate]);

    return (
        <section className='sign-in'>
            <div className='sign-in__locale'>
                <Localization/>
            </div>

            <div className='sign-in__printer'>
                <div className='sign-in__printer-paper'>
                    <div className='sign-in__receipt'>
                        <div className='sign-in__paper'>
                            <img className='sign-in__logo' src={images.newLogo} alt='fiscalBOX'/>
                            <p className='sign-in__company'>{COMPANY_NAME}</p>
                            <p className='sign-in__address'>{COMPANY_ADDRESS}</p>

                            <hr className='sign-in__divider'/>

                            <h1 className='sign-in__title'>{t('signIn.title')}</h1>
                            <ReceiptPart>
                                <ReceiptTypeBadge/>
                                <ReceiptDetails printedAt={printedAt}/>
                            </ReceiptPart>

                            {signInError && (
                                <p className='sign-in__error' role='alert'>
                                    {t(signInError === 'invalid' ? 'signIn.errorInvalid' : 'signIn.errorGeneric')}
                                </p>
                            )}
                            <SignInForm setUser={setUser}/>

                            <ReceiptPart>
                                <div className='sign-in__receipt-blur'>
                                    <ReceiptLine left='Jami' right='12 000.00'/>
                                    <ReceiptLine left='Naqd pul' right='12 000.00'/>
                                </div>
                            </ReceiptPart>

                            <a
                                className='sign-in__help'
                                href={HELP_URL}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <QRCode
                                    className='sign-in__qr'
                                    type='svg'
                                    value={HELP_URL}
                                    size={216}
                                    color='#18181b'
                                    bgColor='#ffffff'
                                />
                                <span className='sign-in__help-text'>
                                    <strong>{t('signIn.needHelp')}</strong>
                                    <span>{t('signIn.scanForHelp')}</span>
                                </span>
                            </a>

                            <p className='sign-in__thanks'>{t('signIn.thankYou')}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SignIn;
